import React, { useEffect, useState } from 'react';
import {
  COMMON_FLOW,
  SIGN_IN_FLOW,
  SIGN_IN_WITH_CODE_FLOW,
  SIGN_UP_HAS_ACCOUNT_FLOW,
  SIGN_UP_NO_ACCOUNT_FLOW,
} from '../constants/flow';
import { AuthStep } from '../types';
import { Auth } from 'aws-amplify';
import { AuthState, getUserAccess } from '../AuthState';
import { useNavigate } from 'react-router-dom';

interface AuthStepContextValue {
  currentStep: AuthStep;
  nextStep: () => void;
  lastStep: () => void;
  isLoading: boolean;
  isLastStep: boolean;
  setIsSignInFlow: (isSignInFlow: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  email: string;
  phoneNumber: string;
  setEmail: (email: string) => void;
  setPhoneNumber: (phoneNumber: string) => void;
  hasAccount: boolean;
  setHasAccount: (hasAccount: boolean) => void;
  resetStep: () => void;
  skipTo: (step: AuthStep) => void;
  pin: string;
  setPin: (pin: string) => void;
  hadTried: () => void;
  retries: number;
}

export const AuthStepContext = React.createContext<AuthStepContextValue>({
  currentStep: AuthStep.EMAIL,
  nextStep: () => {},
  lastStep: () => {},
  isLoading: false,
  isLastStep: true,
  setIsSignInFlow: () => {},
  setIsLoading: () => {},
  email: '',
  setEmail: () => {},
  hasAccount: false,
  setHasAccount: () => {},
  phoneNumber: '',
  setPhoneNumber: () => {},
  resetStep: () => {},
  skipTo: () => {},
  pin: '',
  setPin: () => {},
  hadTried: () => {},
  retries: 0,
});

export const useAuthStep = () => React.useContext(AuthStepContext);

const AuthStepProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [retries, setRetries] = useState(3);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [pin, setPin] = useState('');
  const [hasAccount, setHasAccount] = useState<boolean>(false);
  const [isSignInFlow, setIsSignInFlow] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pendingSkipTo, setPendingSkipTo] = useState<AuthStep | null>(null);

  const hadTried = () => {
    setRetries(retries - 1);
  };
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const userSession = await Auth.currentSession();
        if (userSession.isValid()) {
          const userAccess = await getUserAccess();
          // If API returned an error (not just unregistered user), redirect to error page
          if (userAccess.error) {
            navigate('/error', { replace: true });
            return;
          }
          if (userAccess.registered) {
            if (userAccess.userStatus === 0) {
              setIsSignInFlow(false);
              setHasAccount(true);
              setPendingSkipTo(AuthStep.COMPLETE);
            } else {
              navigate('/', { replace: true });
              return;
            }
          } else {
            setIsSignInFlow(false);
            setHasAccount(false);
            setPendingSkipTo(AuthStep.DETAILS);
          }
        }
      } catch (error) {
        if (error !== 'No current user') {
          console.error(error);
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const resetStep = () => {
    setCurrentIndex(0);
    setIsLoading(false);
    setIsSignInFlow(true);
    setHasAccount(false);
    setPhoneNumber('');
    setEmail('');
    setPin('');
    AuthState.pin = '';
  };

  const currentFlow = React.useMemo(() => {
    if (isSignInFlow) {
      if (pin.length === 4) {
        return [...COMMON_FLOW, ...SIGN_IN_WITH_CODE_FLOW];
      }
      return [...COMMON_FLOW, ...SIGN_IN_FLOW];
    } else {
      return [
        ...COMMON_FLOW,
        ...(hasAccount ? SIGN_UP_HAS_ACCOUNT_FLOW : SIGN_UP_NO_ACCOUNT_FLOW),
      ];
    }
  }, [isSignInFlow, hasAccount]);

  useEffect(() => {
    if (pendingSkipTo && currentFlow.length > 0) {
      const index = currentFlow.indexOf(pendingSkipTo);
      if (index !== -1) {
        setCurrentIndex(index);
        setPendingSkipTo(null);
      }
    }
  }, [currentFlow, pendingSkipTo]);

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const currentStep = React.useMemo(
    () => currentFlow[currentIndex],
    [currentFlow, currentIndex],
  );

  const nextStep = () => {
    setCurrentIndex((prev) => {
      if (prev + 1 >= currentFlow.length) {
        return prev;
      }
      return prev + 1;
    });
  };

  const lastStep = () => {
    setCurrentIndex((prev) => {
      if (prev - 1 < 0) {
        return prev;
      }
      return prev - 1;
    });
  };

  const isLastStep: boolean = React.useMemo(
    () => currentIndex === currentFlow.length - 1,
    [currentIndex, currentFlow],
  );

  return (
    <AuthStepContext.Provider
      value={{
        hadTried,
        retries,
        skipTo: setPendingSkipTo,
        resetStep,
        phoneNumber,
        setPhoneNumber,
        isLoading,
        setIsLoading,
        currentStep,
        nextStep,
        lastStep,
        setIsSignInFlow,
        isLastStep,
        email,
        setEmail,
        hasAccount,
        setHasAccount,
        pin,
        setPin,
      }}
    >
      {children}
    </AuthStepContext.Provider>
  );
};

export default AuthStepProvider;
